import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import {styled} from '@mui/material';
import React, {Fragment, ReactNode, useMemo} from 'react';
import {Collapse} from '../Collapse/Collapse';
import {useId} from '../hooks/useId';
import {List} from '../List/List';
import {NavItem} from './NavItem';
import type {NavItemButtonProps} from './NavItemButton';
import {NavItemButton} from './NavItemButton';
import {composeEventHandlers} from '../utils/composeEventHandlers';
import {NavigationLinkOptions} from './types';
import {navCssClassNames} from './navCssClassNames';

export interface NavItemCollapseProps extends NavItemButtonProps, NavigationLinkOptions {
    /**
     * Array of navigation items
     */
    items?: NavigationLinkOptions[];
    /**
       * Control open state
       */
    open?: boolean;
    /**
       * Slot for end icon in a selected state.
       */
    endIconSelected?: ReactNode;
    /**
       * Toggle Animation
       * @default false
       **/
    disableEndIconAnimation?: boolean;
}

const subItemSelected = (items: NavItemButtonProps[]): boolean => {
    return items.some((subItem: NavItemButtonProps) => subItem.selected);
};

const StyledNavItemButton = styled(NavItemButton)<NavItemCollapseProps>(({
    open, disableEndIconAnimation
}) => ({
    '.MuiButton-endIcon': {
        transitionDuration: '300ms',

        ...open && !disableEndIconAnimation && {
            transform: 'rotate(180deg)'
        }
    }
}));

export const NavItemCollapse = React.forwardRef<unknown, NavItemCollapseProps>(({
    children, startIcon, endIcon, endIconSelected,
    selected, onClick, id, items, ...props
}, ref) => {
    const hasItems = Array.isArray(items) && items.length > 0;
    const [open, setOpen] = React.useState(selected || Boolean(hasItems && subItemSelected(items)));
    const buttonId = useId(id, 'navItemCollapseButton');
    const collapseId = useId(id, 'navItemCollapseList');

    const toggleCollapse = (): void => {
        setOpen(!open);
    };

    const onClickEvents = composeEventHandlers(toggleCollapse, onClick);

    const renderEndIcon = useMemo(() => {
        if (selected && endIconSelected) {
            return endIconSelected;
        }

        return <KeyboardArrowDown/>;
    }, [selected, endIconSelected]);

    return (
        <Fragment>
            <StyledNavItemButton
                {...props}
                aria-controls={collapseId}
                aria-expanded={open}
                as={undefined}
                className={navCssClassNames.itemCollapseButton}
                endIcon={renderEndIcon}
                id={buttonId}
                onClick={onClickEvents} // not the best way to do this
                open={open}
                orientation='side'
                ref={ref}
                selected={selected}
                startIcon={startIcon}
            >
                {children}
            </StyledNavItemButton>
            {hasItems && (
                <Collapse in={open}>
                    <List
                        aria-labelledby={buttonId}
                        className={navCssClassNames.itemCollapseMenu}
                        id={collapseId}
                    >
                        {items.map((item, index) => (
                            <NavItem key={index}>
                                <NavItemButton
                                    level={1}
                                    orientation='side'
                                    {...item}
                                >
                                    {item.label}
                                </NavItemButton>
                            </NavItem>
                        ))}
                    </List>
                </Collapse>
            )}
        </Fragment>
    );
});

NavItemCollapse.displayName = 'NavItemCollapse';
